import { useMemo, useState } from 'react'
import { Head } from '@inertiajs/react'
import { Form, Tabs } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'

import AdminLayout from '@/components/layouts/AdminLayout'
import { PageHeader } from '@/components/patterns'
import {
  Button,
  Table,
  Spin,
  Drawer,
  ConfirmModal,
  Avatar,
  StatusIndicator,
  TableActions,
  Badge,
  message,
} from '@/components/ui'
import InputDebounce from '@/components/forms/InputDebounce'
import EventOwnerForm from './components/EventOwnerForm'
import { Icon } from '@/components/icons'
import { PlusCircleIcon } from '@/components/icons/EventsIcons'
import { api } from '@/lib/apiClient'
import { keys } from '@/lib/queryKeys'
import { Event } from '@/types/events'
import Tooltip from '@/components/Tooltip'

export interface EventOwner {
  id?: string | number
  key?: string
  name: string
  email: string
  mobile?: string
  status: 'active' | 'inactive'
}

interface CreateEventOwnerRequest {
  user: {
    name: string
    email: string
    mobile: string
    status: string
  }
}

interface CreateEventOwnerResponse {
  success: boolean
  user?: EventOwner
  errors?: string[]
}

export default function EventOwners() {
  const [searchText, setSearchText] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('active')
  const [createDrawerOpen, setCreateDrawerOpen] = useState<boolean>(false)
  const [editDrawerOpen, setEditDrawerOpen] = useState<boolean>(false)
  const [currentEventOwner, setCurrentEventOwner] = useState<EventOwner | null>(null)
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState<boolean>(false)
  const [eventOwnerToDelete, setEventOwnerToDelete] = useState<EventOwner | null>(null)
  const [form] = Form.useForm()
  const [editForm] = Form.useForm()

  // Fetch event owners data
  const eventOwnersQuery = useQuery({
    queryKey: keys.eventOwners.list(),
    queryFn: () => api.get<EventOwner[]>('/admin/event_owners'),
  })

  // Fetch events data for owner assignments
  const eventsQuery = useQuery({
    queryKey: keys.events.list(),
    queryFn: () => api.get<Event[]>('/admin/events'),
  })

  // Handle edit button click
  const handleEdit = (eventOwner: EventOwner) => {
    setCurrentEventOwner(eventOwner)
    setEditDrawerOpen(true)
  }

  // Handle delete button click
  const handleDelete = (eventOwner: EventOwner) => {
    setEventOwnerToDelete(eventOwner)
    setDeleteConfirmVisible(true)
  }

  // Handle delete confirmation
  const confirmDelete = () => {
    if (eventOwnerToDelete?.id) {
      deleteEventOwnerMutation.mutate(eventOwnerToDelete.id)
    }
  }

  // Handle delete cancellation
  const cancelDelete = () => {
    setDeleteConfirmVisible(false)
    setEventOwnerToDelete(null)
  }

  // Create event owner mutation
  const createEventOwnerMutation = useMutation({
    mutationFn: (data: any) => {
      const requestData: CreateEventOwnerRequest = {
        user: {
          name: data.name,
          email: data.email,
          mobile: data.mobile,
          status: data.status,
        },
      }
      return api.post<CreateEventOwnerResponse, CreateEventOwnerRequest>(
        '/admin/event_owners',
        requestData
      )
    },
    onSuccess: () => {
      eventOwnersQuery.refetch()
      message.success('Event owner created successfully')
      setCreateDrawerOpen(false)
      form.resetFields()
    },
    onError: (error: any) => {
      if (error.data && error.data.errors) {
        const errorMessages = error.data.errors.join(', ')
        message.error(`Failed to create event owner: ${errorMessages}`)
      } else {
        message.error(`Failed to create event owner: ${error.message}`)
      }
    },
  })

  // Delete event owner mutation
  const deleteEventOwnerMutation = useMutation({
    mutationFn: (id: string | number) => {
      return api.delete<{ success: boolean }>(`/admin/event_owners/${id}`)
    },
    onSuccess: () => {
      eventOwnersQuery.refetch()
      message.success('Event owner deleted successfully')
      setDeleteConfirmVisible(false)
      setEventOwnerToDelete(null)
    },
    onError: (error: any) => {
      if (error.data && error.data.errors) {
        const errorMessages = error.data.errors.join(', ')
        message.error(`Failed to delete event owner: ${errorMessages}`)
      } else {
        message.error(`Failed to delete event owner: ${error.message}`)
      }
      setDeleteConfirmVisible(false)
      setEventOwnerToDelete(null)
    },
  })

  // Update event owner mutation
  const updateEventOwnerMutation = useMutation({
    mutationFn: (data: any) => {
      if (!currentEventOwner?.id) {
        throw new Error('No event owner ID provided for update')
      }

      const requestData: CreateEventOwnerRequest = {
        user: {
          name: data.name,
          email: data.email,
          mobile: data.mobile,
          status: data.status,
        },
      }

      return api.patch<CreateEventOwnerResponse, CreateEventOwnerRequest>(
        `/admin/event_owners/${currentEventOwner.id}`,
        requestData
      )
    },
    onSuccess: () => {
      eventOwnersQuery.refetch()
      message.success('Event owner updated successfully')
      setEditDrawerOpen(false)
      editForm.resetFields()
      setCurrentEventOwner(null)
    },
    onError: (error: any) => {
      if (error.data && error.data.errors) {
        const errorMessages = error.data.errors.join(', ')
        message.error(`Failed to update event owner: ${errorMessages}`)
      } else {
        message.error(`Failed to update event owner: ${error.message}`)
      }
    },
  })

  // Get event owners data from the query
  const eventOwners = eventOwnersQuery.data || []
  const events = eventsQuery.data || []

  const eventsByOwnerId = useMemo(() => {
    const map = new Map<string, Event[]>()
    events.forEach((event) => {
      const ownerIds = event.owner_ids || []
      ownerIds.forEach((ownerId) => {
        const key = ownerId.toString()
        const existing = map.get(key) || []
        existing.push(event)
        map.set(key, existing)
      })
    })
    return map
  }, [events])

  // Count by status for tabs
  const activeCount = eventOwners.filter((owner) => owner.status === 'active').length
  const inactiveCount = eventOwners.filter((owner) => owner.status === 'inactive').length

  // Filter event owners based on search text and status
  const filteredEventOwners = eventOwners.filter((owner) => {
    const matchesSearch =
      owner.name.toLowerCase().includes(searchText.toLowerCase()) ||
      owner.email.toLowerCase().includes(searchText.toLowerCase())
    const matchesStatus =
      statusFilter === 'all' || owner.status === statusFilter
    return matchesSearch && matchesStatus
  })

  // Table columns configuration
  const columns = [
    {
      title: 'Event Owner',
      dataIndex: 'name',
      key: 'name',
      width: '22%',
      sorter: (a: EventOwner, b: EventOwner) => a.name.localeCompare(b.name),
      render: (text: string) => (
        <div className="flex items-center gap-3">
          <Avatar name={text} size="small" />
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: '20%',
      sorter: (a: EventOwner, b: EventOwner) => a.email.localeCompare(b.email),
    },
    {
      title: 'Assigned Events',
      key: 'assigned_events',
      width: '25%',
      render: (_: any, record: EventOwner) => {
        if (eventsQuery.isLoading) {
          return <span className="text-sm text-gray-400">Loading...</span>
        }

        if (eventsQuery.isError) {
          return <span className="text-sm text-gray-400">Unavailable</span>
        }

        const ownerId = record.id?.toString() || ''
        const ownerEvents = eventsByOwnerId.get(ownerId) || []
        const sortedOwnerEvents = [...ownerEvents].sort((a, b) =>
          a.name.localeCompare(b.name)
        )

        if (!sortedOwnerEvents.length) {
          return <span className="text-sm text-gray-400">No events</span>
        }

        return (
          <div className="flex flex-wrap gap-1">
            {sortedOwnerEvents.slice(0, 2).map((event) => (
              <Badge key={event.id ?? event.name} variant="default">
                {event.name}
              </Badge>
            ))}
            {sortedOwnerEvents.length > 2 && (
              <Tooltip
                title={sortedOwnerEvents.slice(2).map((event) => event.name).join(', ')}
                placement="top"
              >
                <Badge variant="default">+{sortedOwnerEvents.length - 2}</Badge>
              </Tooltip>
            )}
          </div>
        )
      },
    },
    {
      title: 'Mobile Number',
      dataIndex: 'mobile',
      key: 'mobile',
      width: '15%',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '8%',
      sorter: (a: EventOwner, b: EventOwner) => a.status.localeCompare(b.status),
      render: (status: string) => (
        <StatusIndicator
          status={status === 'active' ? 'success' : 'inactive'}
          label={status === 'active' ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: EventOwner) => (
        <TableActions
          actions={[
            {
              key: 'edit',
              icon: <Icon name="edit" size={20} />,
              onClick: () => handleEdit(record),
              ariaLabel: 'Edit event owner',
            },
            {
              key: 'delete',
              icon: <Icon name="delete" size={20} />,
              onClick: () => handleDelete(record),
              ariaLabel: 'Delete event owner',
              danger: true,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <AdminLayout>
      <Head title="Event Owners" />
      <div className="w-full bg-[#f5f5f5]">
        <PageHeader
          title="Event Owners"
          subtitle="Add event owners to help manage your events."
          actions={
            <Button
              variant="primary"
              size="small"
              icon={<PlusCircleIcon />}
              onClick={() => setCreateDrawerOpen(true)}
            >
              Add Event Owner
            </Button>
          }
        />

        {/* Status filter tabs */}
        <div className="px-6 pt-4">
          <Tabs
            activeKey={statusFilter}
            onChange={(key) => setStatusFilter(key)}
            size="small"
            items={[
              { key: 'all', label: `All (${eventOwners.length})` },
              { key: 'active', label: `Active (${activeCount})` },
              { key: 'inactive', label: `Inactive (${inactiveCount})` },
            ]}
          />
        </div>

        {/* Search section */}
        <div className="px-6 py-4">
          <div className="w-[235px]">
            <div className="relative">
              <InputDebounce
                placeholder="Search Name or Email"
                onDebouncedChange={(value) => setSearchText(value || '')}
                className="w-full border border-[#d9d9d9] rounded-sm px-3 text-sm h-[32px]"
                style={{ paddingRight: '40px', fontSize: '14px' }}
                size="small"
              />
              <div className="absolute right-0 top-0 h-full flex items-center justify-center px-3 border-l border-[#d9d9d9]">
                <SearchOutlined className="text-[rgba(0,0,0,0.25)]" />
              </div>
            </div>
          </div>
        </div>

        {/* Table section */}
        <div className="p-6 bg-[#f5f5f5]">
          {eventOwnersQuery.isLoading ? (
            <div className="flex justify-center items-center h-64">
              <Spin size="large" />
            </div>
          ) : eventOwnersQuery.isError ? (
            <div className="text-red-500 text-center py-4">
              Error loading event owners: {(eventOwnersQuery.error as Error).message}
            </div>
          ) : (
            <Table
              size="middle"
              columns={columns}
              dataSource={filteredEventOwners}
              rowSelection={{
                type: 'checkbox',
              }}
              pagination={false}
              className="ava-data-table"
              rowClassName="bg-transparent"
              style={{ backgroundColor: 'transparent' }}
            />
          )}
        </div>

        {/* Add Event Owner Drawer */}
        <Drawer
          title="Add Event Owner"
          open={createDrawerOpen}
          onClose={() => setCreateDrawerOpen(false)}
          onSubmit={() => {
            form.validateFields().then((values) => {
              createEventOwnerMutation.mutate(values)
            })
          }}
          submitText="Add"
          loading={createEventOwnerMutation.isPending}
          width={400}
        >
          <EventOwnerForm form={form} />
        </Drawer>

        {/* Edit Event Owner Drawer */}
        <Drawer
          title="Edit Event Owner"
          open={editDrawerOpen}
          onClose={() => {
            setEditDrawerOpen(false)
            setCurrentEventOwner(null)
          }}
          onSubmit={() => {
            editForm.validateFields().then((values) => {
              updateEventOwnerMutation.mutate(values)
            })
          }}
          submitText="Update"
          loading={updateEventOwnerMutation.isPending}
          width={400}
        >
          <EventOwnerForm form={editForm} initialValues={currentEventOwner || undefined} />
        </Drawer>

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          open={deleteConfirmVisible}
          title="Delete Event Owner"
          message={
            eventOwnerToDelete ? (
              <div>
                <p className="mb-3">Are you sure you want to delete this event owner?</p>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="mb-1">
                    <strong>Name:</strong> {eventOwnerToDelete.name}
                  </p>
                  <p className="mb-1">
                    <strong>Email:</strong> {eventOwnerToDelete.email}
                  </p>
                  <p className="mb-0">
                    <strong>Status:</strong> {eventOwnerToDelete.status}
                  </p>
                </div>
                <p className="mt-3 text-gray-500 text-sm">
                  This action cannot be undone. The event owner will be permanently removed from the
                  system.
                </p>
              </div>
            ) : (
              'Are you sure you want to delete this event owner?'
            )
          }
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          confirmText="Delete"
          cancelText="Cancel"
          danger
          loading={deleteEventOwnerMutation.isPending}
        />
      </div>
    </AdminLayout>
  )
}
